import classNames from 'classnames';
import type { ComponentProps, ReactNode } from 'react';
import type { MessageDescriptor } from 'react-intl';
import { FormattedMessage, useIntl } from 'react-intl';

import { css } from '@sorare/css';

import { Horizontal, Vertical } from '#src/atoms/layout/flex/index.tsx';
import { LabelS } from '#src/atoms/typography/index.tsx';
import { fantasy, playerGameStatusLabels } from '#src/lib/glossary.ts';
import type { Props as ThedholsColorProps } from '#src/lib/thresholds.ts';

const bar = css`
  transition:
    height ease-in-out 0.3s,
    opacity ease-in-out 0.2s;
  height: calc(
    max(
      var(--max-bar-height) * (var(--score) / var(--max-score)),
      calc(var(--figure-height) / 2)
    )
  );
  background:
    linear-gradient(to top, var(--c-black) 0%, transparent 100%),
    var(--background-color, var(--c-nd-400));

  width: 100%;
  border-radius: var(--half-unit) var(--half-unit) 0 0;
  position: relative;
  opacity: 0.8;
  display: flex;
  justify-content: center;
  align-items: end;
  padding-bottom: var(--unit);
`;

const box = css`
  height: var(--max-bar-height);
`;

const wrapper = css`
  --game-width: 28px;
  width: var(--game-width);

  position: relative;
  display: flex;
  flex-direction: column;
  margin-top: var(--triple-unit);
  &[role='button'] {
    cursor: pointer;
    &:hover {
      [data-fixture-score-bar] {
        opacity: 0.9;
      }
    }
  }
  &:not(.isHighlighted) {
    opacity: 0.3;
    filter: grayscale(1);
  }
`;

const barContainer = css`
  width: 100%;
  position: relative;
`;

const negativeBar = css`
  height: calc(
    max(
      var(--max-bar-height) * (-1 * var(--score) / var(--max-score)),
      calc(var(--figure-height) / 2)
    )
  );
  background: var(--background-color, var(--c-nd-400));
  width: 100%;
  border-radius: var(--half-unit);
  position: relative;
`;
const score = css`
  text-align: center;
  position: absolute;
  color: var(--c-onBase-light);
  left: 0;
  right: 0;
  font: var(--t-bold) var(--t-14);
  font-family: var(--brand-font);
  border-radius: var(--half-unit);
  height: var(--figure-height);
`;

const positiveScore = css`
  top: calc(-0.5 * var(--figure-height));
  background: var(--background-color, var(--c-nd-400));
`;

const negativeScore = css`
  bottom: calc(-0.5 * var(--figure-height));
  background: var(--background-color, var(--c-nd-400));
`;

const logoWrapper = css`
  margin-top: var(--logo-spacing, var(--unit));
  margin-bottom: var(--logo-spacing, var(--unit));
`;

const dnpLabelWrapper = css`
  display: flex;
  flex-direction: column;
  gap: var(--half-unit);
  margin-bottom: var(--half-unit);
  align-items: center;
`;

const Box = (props: Pick<ComponentProps<'div'>, 'children' | 'aria-label'>) => (
  <Vertical className={box} gap={0} align="center" justify="end" {...props} />
);

const LogoWrapper = ({ children }: { children: ReactNode }) => (
  <Horizontal className={logoWrapper} gap={0} center>
    {children}
  </Horizontal>
);

type Game = {
  id: string;
  playerScore?: number;
  upcoming?: boolean;
  gameLabel?: ReactNode;
  startDate: Date;
  tresholdColor?: ThedholsColorProps;
  barLabel?: MessageDescriptor;
  dnpLabel?: ReactNode;
  barContent?: ReactNode;
  aboveBarContent?: ReactNode;
  onClick?: () => void;
  projectionNode?: ReactNode;
  isHighlighted?: boolean;
};

type Props = {
  game: Game;
};

export const FixtureScoreBar = ({ game }: Props) => {
  const { formatMessage } = useIntl();
  const { color, level } = game.tresholdColor || {};

  return (
    // oxlint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions -- unchanged from the former styled div; role="button" is set when clickable
    <div
      onClick={game.onClick}
      {...(game.onClick ? { role: 'button' } : {})}
      className={classNames(wrapper, { isHighlighted: game.isHighlighted })}
    >
      {game.playerScore === undefined ? (
        <>
          {game.upcoming ? (
            <>
              {!!game.projectionNode && (
                <Box aria-label={formatMessage(fantasy.upcoming)}>
                  {game.projectionNode}
                </Box>
              )}
            </>
          ) : (
            <Box>
              <LabelS bold color="var(--c-onBase-secondary)">
                <span className={dnpLabelWrapper}>
                  {game.dnpLabel || (
                    <FormattedMessage
                      {...playerGameStatusLabels.did_not_play_short}
                    />
                  )}
                </span>
              </LabelS>
            </Box>
          )}
          <LogoWrapper>{game.gameLabel}</LogoWrapper>
        </>
      ) : (
        <>
          {game.playerScore >= 0 ? (
            <>
              <Box>
                <div className="mb-2">{game.aboveBarContent}</div>
                <div
                  className={barContainer}
                  aria-label={game.barLabel ? formatMessage(game.barLabel) : ''}
                  style={
                    {
                      '--score': game.playerScore,
                      '--background-color': color,
                    } as React.CSSProperties
                  }
                >
                  <div className={bar} data-fixture-score-bar>
                    {game.barContent}
                  </div>
                  <div
                    className={classNames(score, positiveScore, 'dark-mode', {
                      'max-score-glow': game.isHighlighted && level === 'max',
                    })}
                  >
                    {Math.floor(game.playerScore)}
                  </div>
                </div>
              </Box>
              {!!game.gameLabel && <LogoWrapper>{game.gameLabel}</LogoWrapper>}
            </>
          ) : (
            <>
              <Box />
              <LogoWrapper>{game.gameLabel}</LogoWrapper>
              <div
                className={barContainer}
                aria-label={game.barLabel ? formatMessage(game.barLabel) : ''}
                style={
                  {
                    '--score': game.playerScore,
                    '--background-color': color,
                    marginBottom: 'calc(0.5 * var(--figure-height))',
                  } as React.CSSProperties
                }
              >
                <div className={negativeBar} />
                <div className={classNames(score, negativeScore)}>
                  {Math.round(game.playerScore)}
                </div>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
};
